import { z } from 'zod';
import { commentMsgSchema, connSchema, commentSchema, itemSchema, strokeSchema, textStyleSchema, } from './types.js';
/**
 * Board mutations are expressed as small, invertible operations.
 *
 * One vocabulary serves three jobs:
 *   - optimistic local application in the client,
 *   - authoritative application + broadcast on the server,
 *   - undo/redo, by inverting an op against the document it was applied to.
 *
 * Conflict resolution is last-write-wins per field. That is the right trade for
 * a canvas: two people dragging the same sticky is rare and self-correcting,
 * whereas a full CRDT would cost far more than it buys here. Ops are applied in
 * the order the server assigns, so every client converges on the same document.
 */
export const COLLECTIONS = ['items', 'strokes', 'conns', 'comments'];
/** Entity schema per collection, so `add` payloads are fully validated. */
const entitySchemas = {
    items: itemSchema,
    strokes: strokeSchema,
    conns: connSchema,
    comments: commentSchema,
};
const patchSchema = z.object({
    id: z.string().min(1).max(64),
    patch: z.record(z.string(), z.unknown()),
});
export const opSchema = z.discriminatedUnion('t', [
    z.object({ t: z.literal('add'), c: z.enum(COLLECTIONS), v: z.array(z.unknown()).min(1).max(1000) }),
    z.object({ t: z.literal('set'), c: z.enum(COLLECTIONS), p: z.array(patchSchema).min(1).max(2000) }),
    z.object({ t: z.literal('del'), c: z.enum(COLLECTIONS), ids: z.array(z.string().min(1).max(64)).min(1).max(2000) }),
    z.object({ t: z.literal('msg'), id: z.string().min(1).max(64), msg: commentMsgSchema }),
    z.object({ t: z.literal('msgdel'), id: z.string().min(1).max(64), msgId: z.string().min(1).max(64) }),
    z.object({ t: z.literal('name'), name: z.string().min(1).max(200) }),
]);
/** Which fields may be patched via `set`, per collection. */
const SETTABLE = {
    items: new Set([
        'x',
        'y',
        'w',
        'h',
        'z',
        'color',
        'text',
        'textStyle',
        'label',
        'size',
        'name',
        'title',
        'url',
        'children',
        'src',
    ]),
    strokes: new Set(['pts', 'color', 'w']),
    conns: new Set(['a', 'b']),
    comments: new Set(['x', 'y']),
};
/**
 * Validate an op's payload against the entity schemas.
 *
 * `opSchema` checks the envelope; this checks the contents, which depend on
 * which collection the op targets. Returns the op with parsed payloads, or
 * throws with a readable message.
 */
export function validateOp(raw) {
    const op = opSchema.parse(raw);
    if (op.t === 'add') {
        const schema = entitySchemas[op.c];
        return { ...op, v: op.v.map((entity) => schema.parse(entity)) };
    }
    if (op.t === 'set') {
        const allowed = SETTABLE[op.c];
        for (const { patch } of op.p) {
            for (const key of Object.keys(patch)) {
                if (!allowed.has(key))
                    throw new Error(`field "${key}" is not settable on ${op.c}`);
            }
            if ('textStyle' in patch)
                patch.textStyle = textStyleSchema.nullable().parse(patch.textStyle);
        }
    }
    return op;
}
const byId = (list) => {
    const map = new Map();
    for (const entity of list)
        map.set(entity.id, entity);
    return map;
};
/**
 * Apply an op, returning a new document. Collections that the op does not
 * touch keep their identity, so React can skip re-rendering them.
 *
 * Applying is deliberately forgiving: patching or deleting a missing entity is
 * a no-op rather than an error, because a peer may have removed it first.
 */
export function applyOp(doc, op) {
    switch (op.t) {
        case 'add': {
            const existing = new Set(doc[op.c].map((e) => e.id));
            const fresh = op.v.filter((e) => !existing.has(e.id));
            if (!fresh.length)
                return doc;
            return { ...doc, [op.c]: [...doc[op.c], ...fresh] };
        }
        case 'set': {
            const patches = new Map(op.p.map((p) => [p.id, p.patch]));
            let changed = false;
            const next = doc[op.c].map((entity) => {
                const patch = patches.get(entity.id);
                if (!patch)
                    return entity;
                changed = true;
                return { ...entity, ...patch };
            });
            return changed ? { ...doc, [op.c]: next } : doc;
        }
        case 'del': {
            const gone = new Set(op.ids);
            const next = doc[op.c].filter((entity) => !gone.has(entity.id));
            if (next.length === doc[op.c].length)
                return doc;
            const out = { ...doc, [op.c]: next };
            // Connectors cannot outlive their endpoints.
            if (op.c === 'items') {
                const live = new Set();
                for (const item of out.items) {
                    live.add(item.id);
                    if (item.type === 'group')
                        for (const child of item.children)
                            live.add(child.id);
                }
                out.conns = out.conns.filter((c) => live.has(c.a) && live.has(c.b));
            }
            return out;
        }
        case 'msg': {
            let changed = false;
            const comments = doc.comments.map((c) => {
                if (c.id !== op.id || c.msgs.some((m) => m.id === op.msg.id))
                    return c;
                changed = true;
                return { ...c, msgs: [...c.msgs, op.msg] };
            });
            return changed ? { ...doc, comments } : doc;
        }
        case 'msgdel': {
            let changed = false;
            const comments = doc.comments.map((c) => {
                if (c.id !== op.id)
                    return c;
                const msgs = c.msgs.filter((m) => m.id !== op.msgId);
                if (msgs.length === c.msgs.length)
                    return c;
                changed = true;
                return { ...c, msgs };
            });
            return changed ? { ...doc, comments } : doc;
        }
        case 'name':
            // Board name lives in the boards table, not the doc; handled by callers.
            return doc;
    }
}
export const applyOps = (doc, ops) => ops.reduce((acc, op) => applyOp(acc, op), doc);
/**
 * Build the op that reverses `op` when applied to `doc`.
 *
 * Must be called with the document as it was *before* the op ran — deletions
 * and field overwrites can only be undone by capturing the prior values.
 */
export function invertOp(doc, op, prevName) {
    switch (op.t) {
        case 'add': {
            const existing = new Set(doc[op.c].map((e) => e.id));
            const ids = op.v.map((e) => e.id).filter((entityId) => !existing.has(entityId));
            return ids.length ? { t: 'del', c: op.c, ids } : null;
        }
        case 'set': {
            const index = byId(doc[op.c]);
            const restore = [];
            for (const { id, patch } of op.p) {
                const before = index.get(id);
                if (!before)
                    continue;
                const prior = {};
                // Null explicitly restores legacy typography over JSON/WebSocket transport.
                for (const key of Object.keys(patch))
                    prior[key] = key === 'textStyle' ? (before[key] ?? null) : before[key];
                restore.push({ id, patch: prior });
            }
            return restore.length ? { t: 'set', c: op.c, p: restore } : null;
        }
        case 'del': {
            const gone = new Set(op.ids);
            const removed = doc[op.c].filter((e) => gone.has(e.id));
            return removed.length ? { t: 'add', c: op.c, v: removed } : null;
        }
        case 'msg': {
            const comment = doc.comments.find((c) => c.id === op.id);
            if (!comment || comment.msgs.some((m) => m.id === op.msg.id))
                return null;
            return { t: 'msgdel', id: op.id, msgId: op.msg.id };
        }
        case 'msgdel': {
            const msg = doc.comments.find((c) => c.id === op.id)?.msgs.find((m) => m.id === op.msgId);
            return msg ? { t: 'msg', id: op.id, msg } : null;
        }
        case 'name':
            return prevName && prevName !== op.name ? { t: 'name', name: prevName } : null;
    }
}
/** Invert a whole transaction: reverse order, each op inverted against the doc it saw. */
export function invertOps(doc, ops, prevName) {
    const inverses = [];
    let cursor = doc;
    for (const op of ops) {
        const inverse = invertOp(cursor, op, prevName);
        if (inverse)
            inverses.push(inverse);
        cursor = applyOp(cursor, op);
    }
    return inverses.reverse();
}
//# sourceMappingURL=ops.js.map